import {Trans} from '@lingui/macro';
import React, {FC, useCallback} from 'react';
import styled, {css} from 'styled-components';

import {useInitialLayoutEffect} from '../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {RepositionPopover} from '../../../../../libs/shared/ui/src/components/popovers/repositionPopover';
import {Anchor} from '../../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import {shadows} from '../../../../../libs/shared/ui/src/styles/colorStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {boxShadow} from '../../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import facePile from '../../../../assets/images/horizontalFacePile.png';
import iconPile from '../../../../assets/images/iconPile.png';
import {SettingsHelpWidgetLink} from './settingsHelpWidgetLink';

/*
 * Props.
 */

export interface SettingsHelpWidgetProps {
  anchor: Anchor;
  onRequestClose: Handler;
  isGlobalAdmin: boolean;
  onResourcesClick: Handler;
  onHelpClick: Handler;
  canEmail: boolean;
  onEmailClick: Handler;
  onOpenClick: Handler;
  useDropdownStyle?: boolean;
}

interface SettingsHelpWidgetStyleProps {
  $useDropdownStyle: boolean;
}
const StyledWidgetDiv = styled.div<SettingsHelpWidgetStyleProps>`
  width: 238px;

  position: relative;
  bottom: 11px;

  ${(p) => applyDropdownStyles(p.$useDropdownStyle)}
`;

function applyDropdownStyles(isDropdown: boolean) {
  if (isDropdown) {
    return css`
      background-color: ${(p) => p.theme.greys.white};
      border-radius: 6px;
      ${shadows.dialogsAndDropdowns};
      bottom: 0;
      margin-top: 4px;
    `;
  }

  return css`
    background-color: ${(p) => p.theme.greys.shade20};
    border-radius: 16px;
    ${(p) => boxShadow(p.theme, `0px 2px 10px rgba(0, 0, 0, 0.2)`)};
  `;
}

const StyledWidgetSecondSectionDiv = styled.section`
  padding-left: 20px;
  padding-right: 30px;

  display: grid;
  grid-template-rows: 17px auto auto auto 21px;
  grid-template-areas:
    '.'
    'title'
    'copy'
    'buttons'
    '.';
`;

type StyledFirstSectionProps = {
  $isScheduling?: boolean;
  $isAdmin: boolean;
};

const StyledFirstSection = styled.section<StyledFirstSectionProps>`
  ${(p) => css`
    background-image: ${p.$isScheduling ? `url(${facePile})` : `url(${iconPile})`};
    background-size: 86px 30px;
    background-repeat: no-repeat;
    background-position: 18px 20px;

    padding-left: 20px;
    padding-right: 42px;

    display: grid;
    grid-template-rows: 67px auto auto auto 20px;
    grid-template-areas:
      '.'
      'title'
      'copy'
      'buttons'
      '.';
  `}
`;

const StyledCloseButton = styled(ChromelessButton)`
  position: absolute;
  top: 5px;
  right: 4px;
  border-radius: 100px;

  width: 26px;
  height: 26px;
  ${center()};

  color: ${(p) => p.theme.greys.shade60};

  &:hover {
    color: ${(p) => p.theme.greys.shade70};
    background: ${(p) => p.theme.alphas.gray10};
  }
`;

const StyledWidgetTitle = styled.div`
  margin: 0;
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.large};
  line-height: 1;
  grid-area: title;
`;

const StyledWidgetCopy = styled.div`
  grid-area: copy;

  color: ${(p) => p.theme.greys.shade70};
  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.verySmall};
  line-height: 19px;
  padding-bottom: 15px;

  ${StyledWidgetTitle} + & {
    padding-top: 5px;
  }

  ${StyledFirstSection} & {
    padding-bottom: 14px;
  }
`;

const StyledSeparatorHr = styled.hr`
  outline: 0;
  border: 0;
  height: 1px;
  width: 100%;
  border-bottom: 1px solid ${(p) => p.theme.alphas.black30};
  margin: 0;
`;

const StyledSettingsHelpWidgetLink = styled(SettingsHelpWidgetLink)`
  margin-left: -2px;

  ${(p) =>
    p.iconName === 'calendarSmall' &&
    css`
      margin-right: -8px;
    `}
`;

const StyledButtonsGroupDiv = styled.div`
  grid-area: buttons;
  padding-top: 16px;

  ${StyledWidgetCopy} + & {
    padding-top: 0;
  }

  ${StyledWidgetTitle} + & {
    padding-top: 17px;
  }

  > div + div {
    margin-top: 8px;
  }
`;

/*
 * Component.
 */

export const SettingsHelpWidget: FC<SettingsHelpWidgetProps> = (props) => {
  const {isGlobalAdmin, canEmail, onOpenClick, onEmailClick, onHelpClick, onResourcesClick} = props;

  // Call `onOpenClick` on first layout effect.
  useInitialLayoutEffect(() => onOpenClick());

  const renderResourcesSection = useCallback(
    () => (
      <StyledFirstSection $isAdmin={isGlobalAdmin}>
        <StyledWidgetTitle>
          <Trans id="settingshelpwidget-resourcetitle">Need resources?</Trans>
        </StyledWidgetTitle>
        <StyledWidgetCopy>
          <Trans id="settingshelpwidget-resourcecopy">
            Webinars, guides, and E-books to help you get the most out of Front
          </Trans>
        </StyledWidgetCopy>
        <StyledButtonsGroupDiv>
          <StyledSettingsHelpWidgetLink onClick={onResourcesClick} iconName="externalLinkSmall">
            <Trans id="settingshelpwidget-resourcebutton">View resources</Trans>
          </StyledSettingsHelpWidgetLink>
        </StyledButtonsGroupDiv>
      </StyledFirstSection>
    ),
    [isGlobalAdmin, onResourcesClick],
  );

  const renderAdminContent = useCallback(
    () => (
      <>
        {renderResourcesSection()}
        <StyledSeparatorHr />
        <StyledWidgetSecondSectionDiv>
          <StyledWidgetTitle>
            <Trans id="settingshelpwidget-adminquestion">Quick question?</Trans>
          </StyledWidgetTitle>
          <StyledButtonsGroupDiv>
            <StyledSettingsHelpWidgetLink onClick={onHelpClick} iconName="externalLinkSmall">
              <Trans id="settingshelpwidget-adminhelpcenter">Go to Help Center</Trans>
            </StyledSettingsHelpWidgetLink>
            {canEmail && (
              <StyledSettingsHelpWidgetLink onClick={onEmailClick} iconName="messageSmall">
                <Trans id="settingshelpwidget-emailsupport">Email support</Trans>
              </StyledSettingsHelpWidgetLink>
            )}
          </StyledButtonsGroupDiv>
        </StyledWidgetSecondSectionDiv>
      </>
    ),
    [canEmail, onEmailClick, onHelpClick, renderResourcesSection],
  );

  const renderUserContent = useCallback(
    () => (
      <>
        {renderResourcesSection()}
        <StyledSeparatorHr />
        <StyledWidgetSecondSectionDiv>
          <StyledWidgetTitle>
            <Trans id="settingshelpwidget-usertitle">Need help?</Trans>
          </StyledWidgetTitle>
          <StyledWidgetCopy>
            <Trans id="settingshelpwidget-usercopy">Search for answers or browse our knowledge base.</Trans>
          </StyledWidgetCopy>
          <StyledButtonsGroupDiv>
            <StyledSettingsHelpWidgetLink onClick={onHelpClick} iconName="externalLinkSmall">
              <Trans id="settingshelpwidget-userbutton">Go to Help Center</Trans>
            </StyledSettingsHelpWidgetLink>
          </StyledButtonsGroupDiv>
        </StyledWidgetSecondSectionDiv>
      </>
    ),
    [onHelpClick, renderResourcesSection],
  );

  // NOTE - the SVGs below are the `closeSmall` and `helpFilledSelected` icons.
  // Due to a problem with the Chromium version of our desktop app, these SVGs
  // do not display properly when using the Icon component.
  // Therefore until we upgrade the Chromium version, we are embedding these SVGs directly.
  return (
    <RepositionPopover
      anchor={props.anchor}
      onRequestClose={props.onRequestClose}
      placement="top-end"
      isInteractive
      isExclusive
    >
      <StyledWidgetDiv $useDropdownStyle={Boolean(props.useDropdownStyle)}>
        {!props.useDropdownStyle && (
          <StyledCloseButton onClick={props.onRequestClose}>
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16">
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeWidth="1.5"
                  d="M4.5 4.5l7 7m0-7l-7 7"
                />
              </svg>
            </svg>
          </StyledCloseButton>
        )}
        {isGlobalAdmin ? renderAdminContent() : renderUserContent()}
      </StyledWidgetDiv>
    </RepositionPopover>
  );
};
